/* Funzioni pure: tutto quello che si puo' provare senza browser sta qui sopra.
   Il confine con l'elemento e' la dichiarazione della classe piu' sotto, e i
   test ci si agganciano cercandone il testo: non nominarla in nessun commento
   qui in cima, o il ritaglio finirebbe prima del codice. */

const TREE_HEIGHT = 2.4;
const TREE_BASE_Y = -0.95;

/**
 * La nuvola non mappata: LED sparsi su un guscio sferico di raggio 1.0-1.75,
 * centrato un filo sopra l'origine perche' lo e' anche la camera.
 */
function buildCloud(n, rnd = Math.random) {
  const out = new Float32Array(n * 3);

  for (let i = 0; i < n; i++) {
    const theta = rnd() * 2 * Math.PI;
    const phi = Math.acos(2 * rnd() - 1);
    const r = 1 + rnd() * 0.75;

    out[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    out[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) + 0.25;
    out[i * 3 + 2] = r * Math.cos(phi);
  }

  return out;
}

/**
 * L'albero mappato: un cono che si chiude a punta in cima. Il 14% dei LED
 * sotto il 88% dell'altezza sborda in fuori, a fare i rami che sporgono - e'
 * quello che toglie all'albero l'aria di solido geometrico.
 */
function buildTree(n, rnd = Math.random) {
  const out = new Float32Array(n * 3);

  for (let i = 0; i < n; i++) {
    const yRel = rnd();
    const y = TREE_BASE_Y + yRel * TREE_HEIGHT;

    let maxRadius = (1 - yRel) * 1.02;
    if (rnd() < 0.14 && yRel < 0.88) maxRadius *= 1.15 + rnd() * 0.25;

    const angle = rnd() * Math.PI * 2;
    const radius = Math.sqrt(rnd()) * maxRadius;

    out[i * 3] = radius * Math.cos(angle);
    out[i * 3 + 1] = y;
    out[i * 3 + 2] = radius * Math.sin(angle);
  }

  return out;
}

const CAM_Y = 0.25;
const CAM_Z = 4.8;
const FOV = 50;
const LED_SIZE = 0.13;
const FOG_DENSITY = 0.05;
const FOG_COLOR = [0.043, 0.047, 0.063]; // #0B0C10 in componenti 0-1

/** Quanti pixel vale una unita' di mondo a distanza 1, per un canvas alto stageH. */
function focalFor(stageH) {
  return stageH / 2 / Math.tan(((FOV / 2) * Math.PI) / 180);
}

/**
 * Da coordinate di mondo a pixel del canvas, passando per la rotazione attorno
 * a Y gia' risolta in `cos` e `sin`.
 *
 * I segni sono quelli di Three: ruota (x, z) in (x cos + z sin, -x sin + z cos)
 * e la camera guarda lungo -z da CAM_Z, quindi la profondita' e' CAM_Z - z'.
 * Col segno girato l'albero ruoterebbe dalla parte sbagliata.
 */
function projectPoint(x, y, z, cos, sin, focal, cx, cy, out) {
  const xr = x * cos + z * sin;
  const zr = -x * sin + z * cos;
  const d = CAM_Z - zr;

  out[0] = cx + (xr * focal) / d;
  out[1] = cy - ((y - CAM_Y) * focal) / d;
  out[2] = d;

  return out;
}

/**
 * Il lato dello sprite, cioe' il gl_PointSize di Three: `size * (H/2) / z`.
 * Usa meta' altezza del canvas e non la focale - la differenza e' 2,1x.
 */
function spriteSide(stageH, depth) {
  return (LED_SIZE * (stageH / 2)) / depth;
}

/** FogExp2: quanto del colore proprio sopravvive alla distanza. */
function fogFactor(depth) {
  return Math.exp(-((FOG_DENSITY * depth) ** 2));
}

function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Dove siamo nella sequenza, da 0 a 1. Vale 0 quando lo Stage si aggancia
 * sotto la barra del menu - non quando il Binario tocca la cima della finestra:
 * fra i due punti c'e' l'altezza della barra, e contarla sfaserebbe tutto.
 */
function trackProgress(rectTop, stageTop, trackH, stageH) {
  const span = trackH - stageH;
  if (span <= 0) return 1;
  return clamp01((stageTop - rectTop) / span);
}

/**
 * Un tween della timeline: `start` e `duration` sono frazioni di sequenza,
 * `power` l'esponente dell'ease out di GSAP (1 = power1.out, 2 = power2.out,
 * 0 = lineare). Serve a far coincidere le dissolvenze con quelle del sorgente.
 */
function ramp(p, start, duration, power = 0) {
  const t = clamp01((p - start) / duration);
  return power > 0 ? 1 - (1 - t) ** (power + 1) : t;
}

/** Quanto la nuvola e' diventata albero: comincia al 4%, finisce al 35%. */
function morphT(p) {
  return clamp01((p - 0.04) / 0.31);
}

/** La conversione di THREE.Color.setHSL, senza portarsi dietro Three. */
function hslToRgb(h, s, l, out) {
  if (s === 0) {
    out[0] = out[1] = out[2] = l;
    return out;
  }

  const q = l <= 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  const hue = (t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * 6 * (2 / 3 - t);
    return p;
  };

  out[0] = hue(h + 1 / 3);
  out[1] = hue(h);
  out[2] = hue(h - 1 / 3);

  return out;
}

/**
 * Il colore di un LED, che dipende da dove siamo nella sequenza:
 *
 *   p < 0.12   non mappato: bianco che respira
 *   p < 0.36   mapping diagnostico: cicli R/G/B, gli altri grigi spenti
 *   p >= 0.36  l'effetto scelto dall'utente
 *
 * Le formule sono quelle del prototipo, riga per riga.
 */
function ledColor(effect, x, y, p, time, i, out) {
  if (p < 0.12) {
    const pulse = 0.85 + Math.sin(time * 2 + i * 0.5) * 0.15;
    out[0] = out[1] = out[2] = pulse;
    return out;
  }

  if (p < 0.36) {
    const slowCycle = time * 2.2 + (i % 9) * 0.9;

    if (Math.sin(slowCycle) > 0.35) {
      const channel = Math.floor(slowCycle / Math.PI + (i % 3)) % 3;
      if (channel === 0) {
        out[0] = 1.0; out[1] = 0.05; out[2] = 0.05;
      } else if (channel === 1) {
        out[0] = 0.05; out[1] = 0.95; out[2] = 0.15;
      } else {
        out[0] = 0.12; out[1] = 0.25; out[2] = 1.0;
      }
    } else {
      // I diodi che in questo istante non sparano restano grigio spento: se
      // fossero bianchi non si distinguerebbe la scansione dal riposo.
      out[0] = 0.18; out[1] = 0.18; out[2] = 0.2;
    }

    return out;
  }

  if (effect === 'snow') {
    const wave = Math.sin(y * 2.6 + time * 1.5);

    if (wave > 0.45) {
      out[0] = out[1] = out[2] = 1;
    } else if (wave > -0.15) {
      const trail = 0.28 + ((wave + 0.15) / 0.6) * 0.72;
      out[0] = out[1] = out[2] = trail;
    } else {
      out[0] = 0.16; out[1] = 0.16; out[2] = 0.18;
    }

    return out;
  }

  if (effect === 'candycane') {
    // Quattro bande su un giro: rosso, separatore sottile, bianco, separatore.
    // Scorrono in diagonale, dall'alto a destra al basso a sinistra.
    const phase = ((y * 2.8 - x * 2.8 + time * 1.6) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);

    if (phase < 2.35) {
      out[0] = 1.0; out[1] = 0.02; out[2] = 0.06;
    } else if (phase < 3.14) {
      out[0] = out[1] = out[2] = 0.2;
    } else if (phase < 5.49) {
      out[0] = out[1] = out[2] = 1;
    } else {
      out[0] = out[1] = out[2] = 0.2;
    }

    return out;
  }

  if (effect === 'rainbow') {
    const hue = (y * 0.45 + time * 0.35) % 1;
    return hslToRgb(Math.abs(hue), 0.95, 0.55, out);
  }

  // sunset
  const t = Math.abs((y * 0.45 - time * 0.38) % 1);
  const sparkle = Math.sin(time * 3.2 + i * 4.9);

  if (sparkle > 0.84 && i % 5 === 0) {
    const twinkle = 0.75 + ((sparkle - 0.84) / 0.16) * 0.25;
    out[0] = twinkle; out[1] = twinkle * 0.94; out[2] = twinkle * 0.82;
  } else {
    out[0] = 1.0;
    out[1] = Math.max(0.04, 0.48 * (1 - t));
    out[2] = Math.max(0.08, 0.72 * t);
  }

  return out;
}

const SPRITE_SIZE = 64;
const CACHE_LIMIT = 512;

/** Un secchio ogni 8 livelli per canale: colori quasi uguali condividono lo sprite. */
function tintKey(r, g, b) {
  return ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
}

/** L'alone del diodo: gli stessi stop del prototipo, disegnati una volta sola. */
function makeSprite(size = SPRITE_SIZE) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;

  const ctx = canvas.getContext('2d');
  const half = size / 2;
  const gradient = ctx.createRadialGradient(half, half, 0, half, half, half);

  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.25, 'rgba(255,255,255,0.95)');
  gradient.addColorStop(0.55, 'rgba(255,255,255,0.35)');
  gradient.addColorStop(0.85, 'rgba(255,248,235,0.1)');
  gradient.addColorStop(1, 'rgba(0,0,0,0)');

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);

  return canvas;
}

/**
 * Sprite gia' tinti, tenuti da parte per colore. Ricolorare un alone costa un
 * canvas nuovo, e senza cache ne servirebbero 400 a frame; con la chiave a 5
 * bit un frame ne chiede qualche decina, e poi sono tutti gia' pronti.
 */
class SpriteCache {
  constructor(size = SPRITE_SIZE) {
    this.size = size;
    this.base = makeSprite(size);
    this.map = new Map();
  }

  get(r, g, b) {
    const key = tintKey(r, g, b);
    const hit = this.map.get(key);
    if (hit) return hit;

    if (this.map.size >= CACHE_LIMIT) this.map.clear();

    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = this.size;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(this.base, 0, 0);
    // 'source-in' tiene l'alpha del gradiente e ne cambia solo il colore.
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = `rgb(${r},${g},${b})`;
    ctx.fillRect(0, 0, this.size, this.size);

    this.map.set(key, canvas);
    return canvas;
  }
}

const GRID_Y = -1.25;
const GRID_SIZE = 16;
const GRID_DIVISIONS = 24;
 
class StringsExperience extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-sxp-track]');
    this.stage = this.querySelector('[data-sxp-stage]');
    this.canvas = this.querySelector('[data-sxp-canvas]');
    this.treeEl = this.querySelector('[data-sxp-tree]');
    this.glow = this.querySelector('[data-sxp-glow]');
    this.hero = this.querySelector('[data-sxp-hero]');
    this.dock = this.querySelector('[data-sxp-dock]');
    this.ctaEl = this.querySelector('[data-sxp-cta]');

    this.ctx = this.canvas && this.canvas.getContext ? this.canvas.getContext('2d') : null;
    // Senza canvas restano immagine, testo e CTA: la sezione perde l'effetto,
    // non il contenuto.
    if (!this.ctx) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mobile = window.matchMedia('(max-width: 749px)').matches;

    const n = parseInt(this.mobile ? this.dataset.ledsMobile : this.dataset.leds, 10) || 400;
    this.count = n;
    this.cloud = buildCloud(n);
    this.tree = buildTree(n);
    this.point = [0, 0, 0];
    this.rgb = [0, 0, 0];
    this.sprites = new SpriteCache();

    this.progress = this.reduced ? 1 : 0;
    this.effect = 'snow';
    this.time = 0;
    this.rotation = 0;

    this.hint = this.querySelector('[data-sxp-hint]');
    this.swatches = [...this.querySelectorAll('[data-sxp-effect]')];

    this.dock.addEventListener('click', (event) => {
      const button = event.target.closest('[data-sxp-effect]');
      if (button) this.setEffect(button.dataset.sxpEffect, button);
    });

    // Dentro un radiogroup il tab entra e esce, e sono le frecce a scegliere:
    // e' quello che si aspetta chi naviga da tastiera o con uno screen reader.
    this.dock.addEventListener('keydown', (event) => {
      const dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
      if (!dir) return;
      event.preventDefault();
      const here = this.swatches.findIndex((b) => b.dataset.sxpEffect === this.effect);
      const next = this.swatches[(here + dir + this.swatches.length) % this.swatches.length];
      this.setEffect(next.dataset.sxpEffect, next);
      next.focus();
    });

    this.resize();
    this.onResize = () => this.resize();
    window.addEventListener('resize', this.onResize, { passive: true });

    if (this.reduced) {
      this.applyTimeline(1);
      this.draw();
      this.updateGlow();
      return;
    }

    // Fuori dallo schermo non c'e' niente da disegnare, e il rAF acceso a
    // vuoto e' batteria buttata su un telefono.
    this.observer = new IntersectionObserver((entries) => {
      entries[0].isIntersecting ? this.start() : this.stop();
    });
    this.observer.observe(this.track);
  }

  disconnectedCallback() {
    this.stop();
    if (this.observer) this.observer.disconnect();
    if (this.onResize) window.removeEventListener('resize', this.onResize);
  }

  /** Di quanto lo Stage parte sotto la cima della finestra: lo dice il CSS. */
  get stageTop() {
    return parseFloat(getComputedStyle(this).getPropertyValue('--sxp-top')) || 0;
  }

  resize() {
    const rect = this.stage.getBoundingClientRect();
    // Su schermi densi il canvas va disegnato piu' grande della sua scatola,
    // ma oltre 2 (1,5 su telefono) non si vede la differenza e si paga sola.
    const dpr = Math.min(window.devicePixelRatio || 1, this.mobile ? 1.5 : 2);

    this.width = rect.width;
    this.height = rect.height;
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.focal = focalFor(rect.height);
  }

  start() {
    if (this.frame) return;
    const loop = () => {
      this.frame = requestAnimationFrame(loop);
      this.tick();
    };
    this.frame = requestAnimationFrame(loop);
  }

  stop() {
    if (!this.frame) return;
    cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  tick() {
    this.time += 0.035;

    const rect = this.track.getBoundingClientRect();
    this.progress = trackProgress(rect.top, this.stageTop, this.track.offsetHeight, this.height);

    // Prima che l'albero sia vestito i LED girano lenti; dopo si assestano
    // frontali, perche' da li' in poi devono combaciare con la foto.
    if (this.progress < 0.36) {
      this.rotation = this.time * 0.2;
    } else {
      this.rotation += (0 - this.rotation) * 0.08;
    }

    this.applyTimeline(this.progress);
    this.draw();
    this.updateGlow();
  }

  /** Le dissolvenze della sequenza, con le stesse finestre e gli stessi ease del prototipo. */
  applyTimeline(p) {
    this.hero.style.opacity = String(1 - ramp(p, 0, 0.12, 1));
    if (this.treeEl) this.treeEl.style.opacity = String(ramp(p, 0.32, 0.12, 2));
    this.dock.style.opacity = String(ramp(p, 0.36, 0.1, 1));
    if (this.ctaEl) this.ctaEl.style.opacity = String(ramp(p, 0.36, 0.1, 1));
    this.gridAlpha = 0.45 * (1 - ramp(p, 0.18, 0.18, 1));

    const live = p >= 0.36;
    this.dock.style.pointerEvents = live ? 'auto' : 'none';
    if (this.ctaEl) this.ctaEl.style.pointerEvents = live ? 'auto' : 'none';

    // L'hint compare una volta sola e se ne va da solo dopo 3s. Il prototipo
    // qui bloccava lo scroll del body: non lo facciamo, su iOS un blocco a
    // meta' gesto e' inaffidabile e la pagina resta incollata.
    if (live && this.hint && !this.hintShown) {
      this.hintShown = true;
      this.hint.hidden = false;
      this.hint.classList.add('is-visible');
      setTimeout(() => this.hint.classList.remove('is-visible'), 3000);
    }
  }

  setEffect(name, button) {
    this.effect = name;

    for (const swatch of this.swatches) {
      const active = swatch === button;
      swatch.classList.toggle('is-active', active);
      swatch.setAttribute('aria-checked', active ? 'true' : 'false');
      swatch.tabIndex = active ? 0 : -1;
    }

    // Con il movimento ridotto non gira nessun ciclo: il frame va chiesto a mano.
    if (this.reduced) this.draw();
  }

  /**
   * La stanza attorno all'albero prende il colore dell'effetto. E' un velo in
   * soft-light sopra la foto: aggiunge luce dove la foto e' chiara e non
   * spiana i neri, che e' quello che fa una luce vera in una stanza.
   */
  updateGlow() {
    if (!this.glow) return;

    if (this.progress < 0.45) {
      this.glow.style.opacity = '0';
      return;
    }

    this.glow.style.opacity = '1';
    const t = this.time;

    if (this.effect === 'snow') {
      const pulse = 0.55 + Math.sin(t * 2) * 0.12;
      this.glow.style.background = `radial-gradient(ellipse at 50% 54%, rgba(255,255,255,${pulse}) 0%, rgba(215,235,255,${pulse * 0.7}) 38%, transparent 72%)`;
    } else if (this.effect === 'candycane') {
      const pulse = 0.65 + Math.sin(t * 2.5) * 0.15;
      this.glow.style.background = `radial-gradient(ellipse at 50% 54%, rgba(255,25,45,${pulse}) 0%, rgba(255,240,245,${pulse * 0.6}) 40%, transparent 72%)`;
    } else if (this.effect === 'rainbow') {
      const deg = (t * 30) % 360;
      this.glow.style.background = `radial-gradient(ellipse at 50% 54%, hsla(${deg},95%,65%,0.7) 0%, hsla(${(deg + 60) % 360},90%,55%,0.45) 42%, transparent 74%)`;
    } else {
      const pulse = 0.65 + Math.sin(t * 2.5) * 0.15;
      this.glow.style.background = `radial-gradient(ellipse at 50% 54%, rgba(255,80,0,${pulse}) 0%, rgba(255,20,100,${pulse * 0.65}) 40%, transparent 74%)`;
    }
  }

  draw() {
    const ctx = this.ctx;
    const cx = this.width / 2;
    const cy = this.height / 2;
    const cos = Math.cos(this.rotation);
    const sin = Math.sin(this.rotation);
    const t = morphT(this.progress);

    ctx.clearRect(0, 0, this.width, this.height);
    if (this.gridAlpha > 0.01) this.drawGrid(cx, cy);

    // Additivo: due aloni sovrapposti si sommano, come le luci vere.
    ctx.globalCompositeOperation = 'lighter';

    for (let i = 0; i < this.count; i++) {
      const j = i * 3;
      const x = this.cloud[j] + (this.tree[j] - this.cloud[j]) * t;
      const y = this.cloud[j + 1] + (this.tree[j + 1] - this.cloud[j + 1]) * t;
      const z = this.cloud[j + 2] + (this.tree[j + 2] - this.cloud[j + 2]) * t;

      projectPoint(x, y, z, cos, sin, this.focal, cx, cy, this.point);
      const depth = this.point[2];
      if (depth < 0.1) continue;

      ledColor(this.effect, x, y, this.progress, this.time, i, this.rgb);

      // Il fog di Three miscela verso il colore di fondo, non abbassa l'alpha.
      const f = fogFactor(depth);
      const r = Math.round((this.rgb[0] * f + FOG_COLOR[0] * (1 - f)) * 255);
      const g = Math.round((this.rgb[1] * f + FOG_COLOR[1] * (1 - f)) * 255);
      const b = Math.round((this.rgb[2] * f + FOG_COLOR[2] * (1 - f)) * 255);

      const side = spriteSide(this.height, depth);
      ctx.drawImage(this.sprites.get(r, g, b), this.point[0] - side / 2, this.point[1] - side / 2, side, side);
    }

    ctx.globalCompositeOperation = 'source-over';
  }

  /**
   * Il piano prospettico indaco sotto la nuvola: c'e' finche' non c'e' l'albero.
   *
   * Non prende la rotazione, e non e' una dimenticanza: nel prototipo la
   * GridHelper e' un oggetto della scena per conto suo, e a girare e' solo il
   * sistema di particelle. E' un pavimento, sta fermo mentre i LED gli ruotano
   * sopra. Qui la rotazione e' un parametro della proiezione invece che una
   * proprieta' dell'oggetto, quindi va passata l'identita' a mano.
   */
  drawGrid(cx, cy) {
    const ctx = this.ctx;
    const half = GRID_SIZE / 2;
    const step = GRID_SIZE / GRID_DIVISIONS;
    const a = this.point;
    const b = [0, 0, 0];

    ctx.save();
    ctx.lineWidth = 1;

    for (let k = 0; k <= GRID_DIVISIONS; k++) {
      const q = -half + k * step;
      const mid = k === GRID_DIVISIONS / 2;
      ctx.strokeStyle = mid ? `rgba(61,63,255,${this.gridAlpha})` : `rgba(39,41,158,${this.gridAlpha})`;

      for (const [ax, az, bx, bz] of [[q, -half, q, half], [-half, q, half, q]]) {
        projectPoint(ax, GRID_Y, az, 1, 0, this.focal, cx, cy, a);
        projectPoint(bx, GRID_Y, bz, 1, 0, this.focal, cx, cy, b);
        if (a[2] < 0.1 || b[2] < 0.1) continue;
        ctx.beginPath();
        ctx.moveTo(a[0], a[1]);
        ctx.lineTo(b[0], b[1]);
        ctx.stroke();
      }
    }

    ctx.restore();
  }
}

customElements.define('strings-experience', StringsExperience);
